<template>
  <Tooltip placement="top" :options="TOOLTIP_OPTIONS">
    <template #trigger>
      <slot />
    </template>
    <template #tooltip>
      <div
        v-if="status !== 'unavailable'"
        class="flex flex-col gap-2 text-left text-xs tabular-nums"
        data-cy="job-cost-breakdown"
      >
        <p v-if="status === 'error'" data-cy="job-cost-breakdown-error">
          The labor breakdown couldn't be loaded. Hover again to retry.
        </p>
        <template v-else-if="status === 'loaded' && jobCost">
          <p class="font-semibold">Labor from completed visits</p>
          <table v-if="jobCost.labor_lines.length" class="w-full">
            <thead>
              <tr>
                <th class="pr-3 font-normal">Technician</th>
                <th class="pr-3 font-normal text-right">Hours</th>
                <th
                  v-if="jobCost.rates_visible"
                  class="pr-3 font-normal text-right"
                  data-cy="job-cost-breakdown-rate-header"
                >
                  Rate / Hr
                </th>
                <th class="font-normal text-right">Labor Cost</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="line in jobCost.labor_lines"
                :key="`${line.person.type}-${line.person.id}`"
                data-cy="job-cost-breakdown-line"
              >
                <td class="pr-3">{{ line.person.name }}</td>
                <td class="pr-3 text-right" data-cy="job-cost-breakdown-hours">
                  {{ (line.total_minutes / 60).toFixed(2) }}
                </td>
                <td
                  v-if="jobCost.rates_visible"
                  class="pr-3"
                  data-cy="job-cost-breakdown-rate"
                >
                  <div class="flex items-center justify-end gap-1">
                    <Badge
                      v-if="line.rate_source === 'department_fallback'"
                      variant="chip"
                      color="yellow"
                      data-cy="job-cost-breakdown-rate-fallback"
                      text="DEPT"
                      :tooltip-text="FALLBACK_RATE_TOOLTIP"
                    />
                    <Badge
                      v-if="line.rate_varies"
                      variant="chip"
                      color="yellow"
                      data-cy="job-cost-breakdown-rate-varies"
                      text="Rate changed"
                      :tooltip-text="RATE_VARIES_TOOLTIP"
                    />
                    <JobCostValue :value="line.rate_cents" />
                  </div>
                </td>
                <td class="text-right" data-cy="job-cost-breakdown-line-cost">
                  <JobCostValue :value="line.labor_cost_cents" />
                </td>
              </tr>
            </tbody>
          </table>
          <p v-else data-cy="job-cost-breakdown-empty">
            {{
              jobCost.state === "times_invalid"
                ? NOT_AVAILABLE_JOB_TIME_TOOLTIP_TEXT
                : "No labor recorded"
            }}
          </p>
          <div
            class="flex justify-between gap-4"
            data-cy="job-cost-breakdown-parts"
          >
            <span>Parts</span>
            <JobCostValue :value="jobCost.parts_cost_cents" />
          </div>
          <div
            class="flex justify-between gap-4"
            data-cy="job-cost-breakdown-finance-fee"
          >
            <span>Finance fee</span>
            <JobCostValue :value="jobCost.finance_fee_cents" />
          </div>
          <div
            v-if="jobCost.purchase_orders_cost_cents !== 0"
            class="flex justify-between gap-4"
            data-cy="job-cost-breakdown-purchase-orders"
          >
            <span>Purchase orders</span>
            <JobCostValue :value="jobCost.purchase_orders_cost_cents" />
          </div>
          <div
            class="flex justify-between gap-4 border-t border-gray-400 pt-1 font-semibold"
            data-cy="job-cost-breakdown-total"
          >
            <span>COGS</span>
            <JobCostValue :value="jobCost.job_cost_cents" />
          </div>
        </template>
        <p v-else data-cy="job-cost-breakdown-loading">Loading labor...</p>
      </div>
    </template>
  </Tooltip>
</template>

<script setup lang="ts">
import { nextTick, ref } from "vue";
import { useStore } from "vuex";

import Badge from "@/components/Badge.vue";
import Tooltip from "@/components/Tooltip.vue";
import CONSTANTS from "@/Constants.js";
import {
  FALLBACK_RATE_TOOLTIP,
  RATE_VARIES_TOOLTIP
} from "@/Job/jobCostMarkers";
import JobCostValue from "@/Job/Tabs/JobCostValue.vue";
import type { JobCost } from "@/typings/jobCost";

// The COGS breakdown behind a report cell.
const props = defineProps<{ jobId: number }>();

type Status = "idle" | "loading" | "loaded" | "error" | "unavailable";
interface Popover {
  hide(): void;
  popperInstance: { update(): void } | null;
}

const { NOT_AVAILABLE_JOB_TIME_TOOLTIP_TEXT } = CONSTANTS;

const store = useStore();
const status = ref<Status>("idle");
const jobCost = ref<JobCost | null>(null);

// hideOnClick: a tap outside closes it on a phone; Tooltip.vue defaults it off.
const TOOLTIP_OPTIONS = { hideOnClick: true, onShow };

// Fetched on first open only; an error retries on the next open.
function onShow(popover: Popover): false | undefined {
  if (status.value === "unavailable") return false;
  if (status.value === "idle" || status.value === "error") load(popover);
  return undefined;
}

async function load(popover: Popover): Promise<void> {
  status.value = "loading";
  try {
    const fetched: JobCost | null = await store.dispatch(
      "fetchJobCost",
      props.jobId
    );
    // null is the flag-off answer: there is nothing to break down.
    if (fetched === null) {
      status.value = "unavailable";
      popover.hide();
      return;
    }
    jobCost.value = fetched;
    status.value = "loaded";
  } catch {
    status.value = "error";
  }
  // tippy placed the popover around "Loading labor..."; re-place it at its loaded size so it stays on screen.
  await nextTick();
  popover.popperInstance?.update();
}
</script>
